import React from 'react';

import useMediaQuery from '@mui/material/useMediaQuery';
import { Controller, useFormContext } from 'react-hook-form';
import colorsVars from 'styles/colors.module.scss';
import scssVariables from 'styles/variables.module.scss';

import { IAssigneeOptionsList } from 'hooks/useAssigneeOptionsList';

import { AssigneeSelect } from 'components/common/AssigneeSelect/AssigneeSelect';
import { DateRangeFilter } from 'components/common/CustomFilters/DateRangeFilter/DateRangeFilter';
import { TabOptions } from 'components/common/CustomFilters/DateRangeFilter/DateRangeFilter.utils';
import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import { WbsFilter } from 'components/common/GlobalFilterComponent/components/WbsFilter/wbsFilter';
import Icon from 'components/common/Icons/icon';
import { InputOptionsWithIcon, MenuList, OptionsWithExclude } from 'components/common/MyCustomSelect/myCustomSelect';
import { listStyleWithExclude } from 'components/common/MyCustomSelect/myCustomSelect.style';
import SwitchComponent from 'components/common/Switch/Switch';
import TextSearchInput from 'components/common/TextSearchInput/textSearchInput';

import {
    IFields,
    IFieldsOptions,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainFilters/MainFilters.types';
import { getInputValue } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainFilters/MainFilters.utils';
import { AdvancedWrapperTooltipContent } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainFilters/advancedWrapperTooltipContent/advancedWrapperTooltipContent';
import { CustomizedRoundIcon, CustomizedTooltip, MyCustomSelect } from 'components/common';

import classes from './InitialFilters.module.scss';

const { iconColor } = colorsVars;

interface IInitialFilters {
    fieldsOptions: IFieldsOptions;
    handleApplyFilters: (data: IFields) => void;
}

export const InitialFilters = ({ fieldsOptions, handleApplyFilters }: IInitialFilters) => {
    const { setValue, getValues, watch, control, handleSubmit } = useFormContext<IFields>();
    const methods = { setValue, control, getValues };

    const executeApplyOnSelection = () => {
        handleSubmit(handleApplyFilters)();
    };

    const { mediaLaptop } = scssVariables;
    const isLaptop = useMediaQuery(`(max-width: ${mediaLaptop})`);

    const endDateExistingData = {
        type: watch('finishDate').type,
        value: watch('finishDate').value.length === 0 ? watch('flags').endDatesRange : watch('finishDate').value,
    };

    const handleFinishDateSelection = (data: { value?: string[] | string; type?: TabOptions }) => {
        if (data.type === 'FIXED_DATE') {
            setValue('finishDate', { value: Array.isArray(data.value) ? data.value.map(String) : [], type: data.type });
            const tempFlags = watch('flags');
            delete tempFlags.endDatesRange;
            setValue('flags', tempFlags);
        } else {
            setValue('finishDate', { value: [], type: data.type });
            setValue('flags', {
                ...watch('flags'),
                endDatesRange: data.value as string,
            });
        }

        executeApplyOnSelection();
    };

    const handleRiskIndicators = (value: readonly ISelectOption<string>[]) => {
        const isAnyRisk = value.find((i) => i.value === 'ANY_INDICATOR');
        if (isAnyRisk) {
            setValue('riskIndicators', [isAnyRisk]);
        } else {
            setValue('riskIndicators', value);
        }

        executeApplyOnSelection();
    };

    return (
        <>
            <div className={classes.selectWrapper}>
                <span className={classes.title}>Activity Type</span>
                <Controller
                    render={({ field }) => (
                        <MyCustomSelect<ISelectOption<string>, true>
                            id={'battlecards-filters-activity-type'}
                            {...field}
                            isMulti
                            options={fieldsOptions.cardActivityTypeOptions}
                            onChange={(value) => {
                                field.onChange(value);
                                executeApplyOnSelection();
                            }}
                        />
                    )}
                    name={`cardActivity`}
                    control={control}
                />
            </div>

            <div className={classes.selectWrapper}>
                <span className={classes.title}>Assignee</span>
                <Controller
                    render={({ field }) => (
                        <AssigneeSelect
                            id={'battlecards-filters-assignee'}
                            {...field}
                            onChange={(value: IAssigneeOptionsList) => {
                                field.onChange(value);
                                executeApplyOnSelection();
                            }}
                            isClearable={true}
                        />
                    )}
                    name={`assignee`}
                    control={control}
                />
            </div>

            <div className={classes.selectWrapper}>
                <span className={classes.title}>Status</span>
                <Controller
                    render={({ field }) => (
                        <MyCustomSelect<ISelectOption<string>, true>
                            id={'battlecards-filters-status'}
                            {...field}
                            isMulti
                            options={fieldsOptions.statusOptions}
                            isClearable={true}
                            components={{
                                Option: InputOptionsWithIcon,
                            }}
                            onChange={(value) => {
                                field.onChange(value);
                                executeApplyOnSelection();
                            }}
                        />
                    )}
                    name={`status`}
                    control={control}
                />
            </div>

            <div className={classes.selectWrapper}>
                <span className={classes.title}>Finish Date</span>
                <DateRangeFilter
                    onSelection={handleFinishDateSelection}
                    existingData={endDateExistingData}
                    inputDefaultValue={getInputValue(endDateExistingData)}
                />
            </div>

            <div className={classes.selectWrapper}>
                <span className={classes.title}>Risk Indicators</span>
                <Controller
                    render={({ field }) => (
                        <MyCustomSelect<ISelectOption<string>, true>
                            id={'battlecards-filters-risk-indicators'}
                            {...field}
                            isMulti
                            options={fieldsOptions.riskIndicatorsOptions}
                            onChange={handleRiskIndicators}
                        />
                    )}
                    name={`riskIndicators`}
                    control={control}
                />
            </div>

            <div className={classes.selectWrapper}>
                <span className={classes.title}>Important</span>
                <Controller
                    render={({ field }) => (
                        <MyCustomSelect<ISelectOption<string>>
                            id={'battlecards-filters-important'}
                            {...field}
                            options={fieldsOptions.importantOptions}
                            isClearable={true}
                            onChange={(value) => {
                                field.onChange(value);
                                executeApplyOnSelection();
                            }}
                        />
                    )}
                    name={`important`}
                    control={control}
                />
            </div>

            <div className={classes.selectWrapper}>
                <span className={classes.title}>Urgent</span>
                <Controller
                    render={({ field }) => (
                        <MyCustomSelect<ISelectOption<string>>
                            id={'battlecards-filters-urgent'}
                            {...field}
                            options={fieldsOptions.cardUrgentOptions}
                            isClearable={true}
                            onChange={(value) => {
                                field.onChange(value);
                                executeApplyOnSelection();
                            }}
                        />
                    )}
                    name={`urgent`}
                    control={control}
                />
            </div>

            <div className={classes.selectWrapper}>
                <span className={classes.title}>Tags</span>
                <Controller
                    render={({ field }) => (
                        <MyCustomSelect<ISelectOption<string>, true>
                            id={'battlecards-filters-tags'}
                            {...field}
                            isMulti
                            options={fieldsOptions.tagsOptions}
                            isClearable={true}
                            onChange={(value) => {
                                field.onChange(value);
                                executeApplyOnSelection();
                            }}
                            components={{
                                Option: OptionsWithExclude,
                            }}
                            styles={listStyleWithExclude}
                        />
                    )}
                    name={`tags`}
                    control={control}
                />
            </div>

            <div className={classes.selectWrapper}>
                <span className={classes.title}>Activity Codes</span>
                <Controller
                    render={({ field }) => (
                        <MyCustomSelect<ISelectOption<{ name: number; isExclude: boolean; alias: string | null }>, true>
                            id={'battlecards-filters-activity-codes'}
                            {...field}
                            isMulti
                            options={fieldsOptions.activityCodesOptions}
                            onChange={(value) => {
                                field.onChange(value);
                                executeApplyOnSelection();
                            }}
                            components={{
                                MenuList: MenuList,
                                Option: OptionsWithExclude,
                            }}
                            styles={listStyleWithExclude}
                        />
                    )}
                    name={'activityCodes'}
                    control={control}
                />
            </div>

            <div className={classes.selectWrapper}>
                <WbsFilter
                    name={'wbs'}
                    methods={methods}
                    handleFilters={executeApplyOnSelection}
                    className={classes.wbsFilterCustomClass}
                />
            </div>

            <div className={`${classes.selectWrapper} ${classes.textSearchInputWrapper}`}>
                <Controller
                    render={({ field }) => (
                        <TextSearchInput
                            id={'battlecards-filters-card-search'}
                            {...field}
                            placeholder={'Search by name or Id...'}
                            handleClear={() => {
                                setValue('cardSearch', '');
                                executeApplyOnSelection();
                            }}
                            maxLength={300}
                            handleClick={() => {
                                executeApplyOnSelection();
                            }}
                        />
                    )}
                    name={`cardSearch`}
                    control={control}
                />

                <div className={classes.advancedWrapper}>
                    <SwitchComponent
                        checked={watch('flags').isAdvancedTextSearch ? watch('flags').isAdvancedTextSearch : false}
                        onChange={() => {
                            setValue('flags', {
                                ...watch('flags'),
                                isAdvancedTextSearch: !watch('flags').isAdvancedTextSearch,
                            });
                        }}
                    />
                    <div className={classes.title}>Advanced</div>
                    <CustomizedTooltip
                        tooltipPosition={TooltipPosition.Bottom}
                        contentStyles={{
                            maxWidth: isLaptop ? '400px' : '650px',
                        }}
                        key={'advancedInfo'}
                        tooltipContent={<AdvancedWrapperTooltipContent />}
                        triggerElement={
                            <CustomizedRoundIcon enableHover={true} size={'2.4rem'}>
                                <Icon name={'info-icon'} size="14" color={iconColor} />
                            </CustomizedRoundIcon>
                        }
                    />
                </div>
            </div>
        </>
    );
};
